import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Loader2, Search, Repeat, Wallet, TrendingUp } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rp } from "@/lib/payroll";
import { useT, translateJabatan } from "@/i18n";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { JaritBadge } from "@/components/JaritBadge";
import { EmployeeName } from "@/components/EmployeeName";

export default function KasbonPage() {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/kasbon/employees").then((r) => setList(r.data.employees || [])).catch((e) => toast.error(errMsg(e)));
    api.get("/kasbon/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const l = list || [];
    if (!s) return l;
    return l.filter((e) => (e.nama || "").toLowerCase().includes(s) || (e.emp_code || "").toLowerCase().includes(s));
  }, [list, q]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => (b.balance || 0) - (a.balance || 0)), [filtered]);
  const totalOut = (list || []).reduce((a, b) => a + (b.balance > 0 ? b.balance : 0), 0);
  const withDebt = (list || []).filter((e) => e.balance > 0).length;
  const clear = (list || []).length - withDebt;

  return (
    <div data-testid="kasbon-page" className="space-y-7">
      <div>
        <h1 className="text-[26px] font-semibold tracking-tight sm:text-[30px]">{t("kasbon.title")}</h1>
        <p className="mt-1.5 max-w-2xl text-[14px] leading-relaxed text-[#8E8E93]">{t("kasbon.subtitle")}</p>
      </div>

      {list !== null && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="lg-tint p-5" style={{ background: "linear-gradient(135deg, rgba(192,54,44,0.16), rgba(192,54,44,0.04))" }}>
            <p className="text-[13px] font-medium text-[#8E8E93]">{t("kasbon.stat.total")}</p>
            <p data-testid="kasbon-total-outstanding" className="num mt-1.5 text-[24px] font-semibold tracking-tight text-[#C0362C]">Rp {rp(totalOut)}</p>
          </div>
          <div className="lg-tint p-5" style={{ background: "linear-gradient(135deg, rgba(0,113,227,0.16), rgba(0,113,227,0.04))" }}>
            <p className="text-[13px] font-medium text-[#8E8E93]">{t("kasbon.stat.withDebt")}</p>
            <p className="num mt-1.5 text-[24px] font-semibold tracking-tight text-[#0071E3]">{t("kasbon.people", { n: withDebt })}</p>
          </div>
          <div className="lg-tint p-5" style={{ background: "linear-gradient(135deg, rgba(14,138,70,0.16), rgba(14,138,70,0.04))" }}>
            <p className="text-[13px] font-medium text-[#8E8E93]">{t("kasbon.stat.clear")}</p>
            <p className="num mt-1.5 text-[24px] font-semibold tracking-tight text-[#0E8A46]">{t("kasbon.people", { n: clear })}</p>
          </div>
        </div>
      )}

      {stats && (stats.uang > 0 || stats.barang > 0 || (stats.trend || []).length > 0) && (
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
          <div className="liquid-glass-card flex flex-col p-5" data-testid="kasbon-dash-top">
            <h3 className="mb-3 text-[14px] font-semibold tracking-tight">{t("kasbon.dashTop")}</h3>
            {(stats.debtors || []).length === 0 ? <p className="grid flex-1 place-items-center py-8 text-center text-[13px] text-[#8E8E93]">—</p> : (
              <ol className="flex flex-1 flex-col justify-center space-y-3">
                {stats.debtors.map((d, i) => {
                  const max = Math.max(1, ...stats.debtors.map((x) => x.balance));
                  return (
                    <li key={i}>
                      <div className="flex items-baseline justify-between gap-2"><span className="truncate text-[13px] font-medium">{d.nama}</span><span className="num shrink-0 text-[12.5px] font-semibold text-[#C0362C]">Rp {rp(d.balance)}</span></div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-black/[0.05]"><div className="h-full rounded-full bg-[#C0362C]" style={{ width: `${(d.balance / max) * 100}%` }} /></div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <div className="liquid-glass-card flex flex-1 flex-col justify-center p-5" data-testid="kasbon-dash-split">
              <div className="mb-2 flex items-center gap-2"><Wallet className="h-4 w-4 text-[#0071E3]" /><h3 className="text-[14px] font-semibold tracking-tight">{t("kasbon.dashSplit")}</h3></div>
              <div className="flex items-center gap-4">
                <div className="relative h-[140px] w-[140px] shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <defs>
                        <linearGradient id="kbCash" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#4DA3FF" /><stop offset="100%" stopColor="#0062CC" />
                        </linearGradient>
                        <linearGradient id="kbGoods" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#F06A60" /><stop offset="100%" stopColor="#A82A21" />
                        </linearGradient>
                      </defs>
                      <Pie data={[{ name: t("kasbon.bonUang"), value: stats.uang }, { name: t("kasbon.bonBarang"), value: stats.barang }]} dataKey="value" innerRadius={46} outerRadius={68} paddingAngle={4} cornerRadius={7} stroke="none">
                        <Cell fill="url(#kbCash)" /><Cell fill="url(#kbGoods)" />
                      </Pie>
                      <Tooltip formatter={(v) => `Rp ${rp(v)}`} contentStyle={{ borderRadius: 12, border: "1px solid #00000010", fontSize: 13, boxShadow: "0 8px 24px #00000014" }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-[#A1A1A6]">Total</span>
                    <span className="num text-[12.5px] font-bold text-[#1D1D1F]">{rp(stats.uang + stats.barang)}</span>
                  </div>
                </div>
                <div className="min-w-0 flex-1 space-y-2.5">
                  <div><p className="flex items-center gap-1.5 text-[11px] font-semibold text-[#0071E3]"><span className="h-2.5 w-2.5 rounded-full bg-[#0071E3]" />{t("kasbon.bonUang")}</p><p className="num text-[16px] font-bold">Rp {rp(stats.uang)}</p></div>
                  <div><p className="flex items-center gap-1.5 text-[11px] font-semibold text-[#C0362C]"><span className="h-2.5 w-2.5 rounded-full bg-[#C0362C]" />{t("kasbon.bonBarang")}</p><p className="num text-[16px] font-bold">Rp {rp(stats.barang)}</p></div>
                </div>
              </div>
            </div>

            <div className="liquid-glass-card flex flex-1 flex-col justify-center p-5" data-testid="kasbon-dash-trend">
              <div className="mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#0E8A46]" /><h3 className="text-[14px] font-semibold tracking-tight">{t("kasbon.dashTrend")}</h3></div>
              <div className="h-[150px] w-full">
                {(stats.trend || []).length === 0 ? <div className="grid h-full place-items-center text-[13px] text-[#8E8E93]">—</div> : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.trend} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
                      <defs>
                        <linearGradient id="kbBar" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1FC977" /><stop offset="100%" stopColor="#0C7A3D" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke="#0000000A" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8E8E93" }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#8E8E93" }} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => v >= 1e6 ? `${(v / 1e6).toFixed(0)}jt` : v >= 1e3 ? `${Math.round(v / 1e3)}rb` : v} />
                      <Tooltip formatter={(v) => [`Rp ${rp(v)}`, t("kasbon.paid")]} cursor={{ fill: "#0E8A460D", radius: 8 }} contentStyle={{ borderRadius: 12, border: "1px solid #00000010", fontSize: 13, boxShadow: "0 8px 24px #00000014" }} />
                      <Bar dataKey="total" radius={[8, 8, 0, 0]} fill="url(#kbBar)" maxBarSize={34} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative w-full">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#B0B0B8]" />
        <input data-testid="kasbon-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("kasbon.search")}
          className="focus-ring h-12 w-full rounded-full border border-[#E1E1E6] bg-white pl-11 pr-4 text-[15px] placeholder:text-[#B0B0B8]" />
      </div>

      {list === null ? (
        <div className="flex items-center gap-2 py-24 text-[14px] text-[#8E8E93]"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sorted.map((e, i) => {
            const owes = e.balance > 0;
            const tone = owes ? "#C0362C" : "#0E8A46";
            return (
              <button key={e.employee_id} data-testid={`kasbon-emp-${e.employee_id}`} onClick={() => navigate(`/kasbon/${e.employee_id}`)}
                className="card-soft lift fade-up group p-5 text-left" style={{ animationDelay: `${i * 35}ms` }}>
                <div className="flex items-center gap-3.5">
                  <EmployeeAvatar id={e.employee_id} nama={e.nama} hasPhoto={e.has_photo} size={46} />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 truncate text-[16px] font-semibold tracking-tight">
                      <EmployeeName id={e.employee_id} name={e.nama} testid={`kasbon-name-${e.employee_id}`} />
                      <JaritBadge show={e.is_jarit} />
                    </p>
                    <p className="num truncate text-[12px] text-[#8E8E93]">{e.emp_code || "\u2014"} · {translateJabatan(lang, e.jabatan)}</p>
                  </div>
                </div>
                <div className="mt-4 flex items-end justify-between border-t border-black/[0.06] pt-4">
                  <div>
                    <p className="num text-[22px] font-semibold tracking-tight" style={{ color: tone }}>Rp {rp(e.balance)}</p>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-[#A1A1A6]">{owes ? t("kasbon.sisa") : t("kasbon.lunas")}</p>
                  </div>
                  {e.cicilan > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#EAF2FE] px-2.5 py-1 text-[11px] font-semibold text-[#0071E3]">
                      <Repeat className="h-3 w-3" /> {t("kasbon.cicil", { amt: `Rp ${rp(e.cicilan)}` })}
                    </span>
                  ) : (
                    <ChevronRight className="h-5 w-5 text-[#C8C8CE] transition-transform group-hover:translate-x-0.5" />
                  )}
                </div>
              </button>
            );
          })}
          {sorted.length === 0 && <p className="card-soft col-span-full px-6 py-16 text-center text-[14px] text-[#8E8E93]">{t("kasbon.none")}</p>}
        </div>
      )}
    </div>
  );
}
